import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { notify } from "../../utils/notify";
import CollectionTable from "./CollectionTable";
import RelationshipCell from "./RelationshipCell";
import { VideoThumbnail, Entity, SortConfig, ColumnConfig, VideoStatus } from "../../types/cms";
import { useVideosWithEntities } from "../../hooks/useVideosWithEntities";
import { useTaxonomies } from "../../hooks/useTaxonomies";
import { bulkUpdateVideos, BulkPatch, deleteVideos } from "../../lib/videoAdmin";
import { fallbackThumbnail } from "../../lib/videoSources";
import { dangerButton, inputClass, Modal, primaryButton, secondaryButton, StatusBadge } from "../admin/ui";
import EntityPicker from "../admin/EntityPicker";
import { TAXONOMY_LABELS, TaxonomyKey } from "../../lib/taxonomies";

type StatusFilter = "all" | VideoStatus | "legacy";

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "all", label: "Toutes" },
  { key: "draft", label: "Brouillons" },
  { key: "published", label: "Publiées" },
  { key: "legacy", label: "Sans statut" },
];

interface Props {
  /** Pre-filter on a brand or studio id (from the brands/studios tabs). */
  entityFilter?: { id: string; name: string } | null;
  onClearEntityFilter?: () => void;
}

export default function VideosCollectionView({ entityFilter, onClearEntityFilter }: Props) {
  const navigate = useNavigate();
  const { videos, setVideos, companies, agencies, loading, refetch } = useVideosWithEntities("admin");
  const { taxonomies } = useTaxonomies();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDialog, setBulkDialog] = useState<null | "brand" | "studio" | "taxonomy" | "delete">(null);
  const [bulkEntityId, setBulkEntityId] = useState("");
  const [bulkTaxonomy, setBulkTaxonomy] = useState<{ key: TaxonomyKey; value: string }>({ key: "industries", value: "" });

  const counts = useMemo(() => {
    const c = { all: videos.length, draft: 0, published: 0, legacy: 0 };
    videos.forEach((v) => {
      if (!v.status) c.legacy++;
      else c[v.status]++;
    });
    return c;
  }, [videos]);

  const filteredVideos = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    return videos.filter((video) => {
      if (statusFilter === "legacy" ? !!video.status : statusFilter !== "all" && video.status !== statusFilter) {
        return false;
      }
      if (entityFilter && video.enterpriseId !== entityFilter.id && video.agencyId !== entityFilter.id) {
        return false;
      }
      if (!query) return true;
      return (
        video.title?.toLowerCase().includes(query) ||
        video.videoFor?.name?.toLowerCase().includes(query) ||
        video.producedBy?.name?.toLowerCase().includes(query) ||
        video.tags?.some((t) => t.toLowerCase().includes(query))
      );
    });
  }, [videos, searchQuery, statusFilter, entityFilter]);

  const sortedVideos = useMemo(() => {
    if (!sortConfig) return filteredVideos;
    const value = (v: VideoThumbnail): string | number => {
      switch (sortConfig.field) {
        case "title":
          return v.title?.toLowerCase() || "";
        case "videoFor":
          return v.videoFor?.name?.toLowerCase() || "";
        case "producedBy":
          return v.producedBy?.name?.toLowerCase() || "";
        case "status":
          return v.status || "";
        case "createdAt":
          return v.createdAt?.seconds || 0;
        default:
          return 0;
      }
    };
    return [...filteredVideos].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredVideos, sortConfig]);

  const selectedVideos = videos.filter((v) => selectedIds.has(v.id));

  const handleSort = (field: string) => {
    setSortConfig((current) =>
      current?.field === field
        ? { field, direction: current.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "asc" },
    );
  };

  const toggleAll = () => {
    const allVisibleSelected = sortedVideos.length > 0 && sortedVideos.every((v) => selectedIds.has(v.id));
    setSelectedIds(allVisibleSelected ? new Set() : new Set(sortedVideos.map((v) => v.id)));
  };

  const toggleOne = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const handleRelationshipUpdate = (videoId: string, field: string, newEntity: Entity) => {
    setVideos((prev: VideoThumbnail[]) =>
      prev.map((v) =>
        v.id === videoId
          ? { ...v, [field]: newEntity.id, [field === "agencyId" ? "producedBy" : "videoFor"]: newEntity }
          : v,
      ),
    );
  };

  const runBulk = async (label: string, action: () => Promise<void>) => {
    setBulkBusy(true);
    try {
      await action();
      notify({ isSuccess: true, message: label });
      setSelectedIds(new Set());
      setBulkDialog(null);
      refetch();
    } catch (error) {
      notify({
        isSuccess: false,
        message: "L'action groupée a échoué",
        info: error instanceof Error ? error.message : "Réessayez dans un instant.",
      });
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkPatch = (patch: BulkPatch, label: string) =>
    runBulk(label, () => bulkUpdateVideos(selectedVideos, patch));

  const handleDelete = async (video: VideoThumbnail) => {
    if (!window.confirm(`Supprimer définitivement « ${video.title} » ? Cette action est irréversible.`)) return;
    try {
      await deleteVideos([video.id]);
      setVideos((prev: VideoThumbnail[]) => prev.filter((v) => v.id !== video.id));
      notify({ isSuccess: true, message: "Vidéo supprimée" });
    } catch (error) {
      notify({
        isSuccess: false,
        message: "Suppression impossible",
        info: error instanceof Error ? error.message : "Réessayez dans un instant.",
      });
    }
  };

  const columns: ColumnConfig<VideoThumbnail>[] = [
    { key: "thumbnail", label: "Miniature", width: "w-24" },
    { key: "title", label: "Titre", sortable: true },
    { key: "status", label: "Statut", sortable: true, width: "w-28" },
    { key: "videoFor", label: "Marque", sortable: true },
    { key: "producedBy", label: "Studio", sortable: true },
    { key: "industries", label: "Industries" },
    { key: "createdAt", label: "Ajoutée", sortable: true, width: "w-28" },
  ];

  const renderRow = (video: VideoThumbnail) => {
    const isSelected = selectedIds.has(video.id);
    const thumb = video.thumbnailURL || fallbackThumbnail(video.videoURL) || video.styleframesURLs[0];
    return (
      <tr key={video.id} className={`hover:bg-neutral-50 transition-colors ${isSelected ? "bg-neutral-100" : ""}`}>
        <td className="px-3 py-3">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleOne(video.id)}
            aria-label={`Sélectionner ${video.title}`}
            className="w-4 h-4 rounded border-gray-300 text-neutral-900 focus:ring-neutral-900 cursor-pointer"
          />
        </td>
        <td className="px-4 py-3">
          {thumb ? (
            <img src={thumb} alt="" loading="lazy" className="w-20 h-12 object-cover rounded border border-gray-200" />
          ) : (
            <div className="w-20 h-12 rounded border border-dashed border-gray-300" />
          )}
        </td>
        <td className="px-4 py-3 min-w-[16rem]">
          <Link to={`/admin/video/${video.id}`} className="text-sm font-medium text-gray-900 hover:underline">
            {video.title || "Sans titre"}
          </Link>
          {video.videoFileURL && <div className="text-xs text-neutral-500">Fichier hébergé</div>}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={video.status} />
        </td>
        <td className="px-4 py-3">
          <RelationshipCell
            videoId={video.id}
            field="enterpriseId"
            currentEntity={video.videoFor?.id ? video.videoFor : null}
            allEntities={companies}
            onUpdate={handleRelationshipUpdate}
            onRollback={() => refetch()}
          />
        </td>
        <td className="px-4 py-3">
          <RelationshipCell
            videoId={video.id}
            field="agencyId"
            currentEntity={video.producedBy?.id ? video.producedBy : null}
            allEntities={agencies}
            onUpdate={handleRelationshipUpdate}
            onRollback={() => refetch()}
          />
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
            {video.industries.slice(0, 2).map((industry) => (
              <span key={industry} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-gray-700">
                {industry}
              </span>
            ))}
            {video.industries.length > 2 && <span className="text-xs text-gray-500">+{video.industries.length - 2}</span>}
            {video.industries.length === 0 && <span className="text-xs text-amber-700">À classer</span>}
          </div>
        </td>
        <td className="px-4 py-3 text-xs text-neutral-600 tabular-nums whitespace-nowrap">
          {video.createdAt?.toDate?.().toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" }) ?? "—"}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-3">
            <button onClick={() => navigate(`/admin/video/${video.id}`)} className="text-sm text-neutral-700 hover:text-neutral-950 underline">
              Modifier
            </button>
            <button onClick={() => handleDelete(video)} className="text-sm text-red-700 hover:text-red-900 underline">
              Supprimer
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-4 pb-24">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1 p-1 bg-neutral-200/70 rounded-md">
          {STATUS_FILTERS.filter((f) => f.key !== "legacy" || counts.legacy > 0).map((f) => (
            <button
              key={f.key}
              onClick={() => {
                setStatusFilter(f.key);
                setSelectedIds(new Set());
              }}
              className={`px-3 py-1.5 text-sm rounded ${
                statusFilter === f.key ? "bg-white shadow-sm font-medium" : "text-neutral-600 hover:text-neutral-900"
              }`}
            >
              {f.label} <span className="tabular-nums text-neutral-500">{counts[f.key]}</span>
            </button>
          ))}
        </div>
        {entityFilter && (
          <span className="inline-flex items-center gap-2 text-sm bg-neutral-900 text-white rounded-full pl-3 pr-1 py-1">
            {entityFilter.name}
            <button onClick={onClearEntityFilter} className="rounded-full bg-white/20 hover:bg-white/30 w-5 h-5 leading-none" aria-label="Retirer le filtre">
              ×
            </button>
          </span>
        )}
        <input
          type="search"
          placeholder="Rechercher par titre, marque, studio ou tag…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`${inputClass} flex-1 min-w-[220px]`}
        />
        <span className="text-sm text-gray-600 tabular-nums">
          {sortedVideos.length} vidéo{sortedVideos.length !== 1 ? "s" : ""}
        </span>
      </div>

      <CollectionTable
        data={sortedVideos}
        columns={columns}
        selectedIds={selectedIds}
        onSelectAll={toggleAll}
        onSelectItem={toggleOne}
        sortConfig={sortConfig}
        onSort={handleSort}
        renderRow={renderRow}
        loading={loading}
      />

      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[1500] bg-neutral-950 text-white px-4 py-3 rounded-lg shadow-2xl flex flex-wrap items-center gap-2 max-w-[calc(100vw-2rem)]">
          <span className="text-sm font-medium mr-2 tabular-nums">
            {selectedIds.size} sélectionnée{selectedIds.size > 1 ? "s" : ""}
          </span>
          <button disabled={bulkBusy} className="text-sm px-3 py-1.5 rounded bg-white text-neutral-950 hover:bg-neutral-200" onClick={() => bulkPatch({ status: "published" }, "Vidéos publiées")}>
            Publier
          </button>
          <button disabled={bulkBusy} className="text-sm px-3 py-1.5 rounded hover:bg-white/10" onClick={() => bulkPatch({ status: "draft" }, "Vidéos repassées en brouillon")}>
            Dépublier
          </button>
          <button disabled={bulkBusy} className="text-sm px-3 py-1.5 rounded hover:bg-white/10" onClick={() => { setBulkEntityId(""); setBulkDialog("brand"); }}>
            Changer la marque
          </button>
          <button disabled={bulkBusy} className="text-sm px-3 py-1.5 rounded hover:bg-white/10" onClick={() => { setBulkEntityId(""); setBulkDialog("studio"); }}>
            Changer le studio
          </button>
          <button disabled={bulkBusy} className="text-sm px-3 py-1.5 rounded hover:bg-white/10" onClick={() => setBulkDialog("taxonomy")}>
            Ajouter une catégorie
          </button>
          <button disabled={bulkBusy} className="text-sm px-3 py-1.5 rounded text-red-300 hover:bg-red-500/20" onClick={() => setBulkDialog("delete")}>
            Supprimer
          </button>
          <button className="text-sm px-2 py-1.5 text-neutral-400 hover:text-white" onClick={() => setSelectedIds(new Set())}>
            Annuler
          </button>
        </div>
      )}

      <Modal
        open={bulkDialog === "brand" || bulkDialog === "studio"}
        onClose={() => setBulkDialog(null)}
        title={bulkDialog === "brand" ? `Changer la marque de ${selectedIds.size} vidéo(s)` : `Changer le studio de ${selectedIds.size} vidéo(s)`}
        footer={
          <>
            <button className={secondaryButton} onClick={() => setBulkDialog(null)}>Annuler</button>
            <button
              className={primaryButton}
              disabled={!bulkEntityId || bulkBusy}
              onClick={() =>
                bulkPatch(
                  bulkDialog === "brand" ? { enterpriseId: bulkEntityId } : { agencyId: bulkEntityId },
                  bulkDialog === "brand" ? "Marque mise à jour" : "Studio mis à jour",
                )
              }
            >
              Appliquer
            </button>
          </>
        }
      >
        <EntityPicker
          label={bulkDialog === "brand" ? "Nouvelle marque" : "Nouveau studio"}
          kind={bulkDialog === "brand" ? "companies" : "agencies"}
          companies={companies}
          agencies={agencies}
          value={bulkEntityId}
          onChange={setBulkEntityId}
          onCreated={() => refetch()}
        />
      </Modal>

      <Modal
        open={bulkDialog === "taxonomy"}
        onClose={() => setBulkDialog(null)}
        title={`Ajouter une catégorie à ${selectedIds.size} vidéo(s)`}
        footer={
          <>
            <button className={secondaryButton} onClick={() => setBulkDialog(null)}>Annuler</button>
            <button
              className={primaryButton}
              disabled={!bulkTaxonomy.value || bulkBusy}
              onClick={() =>
                bulkPatch(
                  {
                    addIndustries: bulkTaxonomy.key === "industries" ? [bulkTaxonomy.value] : undefined,
                    addFormats: bulkTaxonomy.key === "formats" ? [bulkTaxonomy.value] : undefined,
                    addStyles: bulkTaxonomy.key === "styles" ? [bulkTaxonomy.value] : undefined,
                  },
                  `« ${bulkTaxonomy.value} » ajouté`,
                )
              }
            >
              Ajouter
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <select
            className={inputClass}
            value={bulkTaxonomy.key}
            onChange={(e) => setBulkTaxonomy({ key: e.target.value as TaxonomyKey, value: "" })}
          >
            {(Object.keys(TAXONOMY_LABELS) as TaxonomyKey[]).map((k) => (
              <option key={k} value={k}>{TAXONOMY_LABELS[k]}</option>
            ))}
          </select>
          <select
            className={inputClass}
            value={bulkTaxonomy.value}
            onChange={(e) => setBulkTaxonomy((prev) => ({ ...prev, value: e.target.value }))}
          >
            <option value="">Choisir…</option>
            {taxonomies[bulkTaxonomy.key].map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          <p className="text-xs text-neutral-500">Les catégories déjà présentes sur chaque vidéo sont conservées.</p>
        </div>
      </Modal>

      <Modal
        open={bulkDialog === "delete"}
        onClose={() => setBulkDialog(null)}
        title={`Supprimer ${selectedIds.size} vidéo(s) ?`}
        footer={
          <>
            <button className={secondaryButton} onClick={() => setBulkDialog(null)}>Annuler</button>
            <button
              className={dangerButton}
              disabled={bulkBusy}
              onClick={() => runBulk("Vidéos supprimées", () => deleteVideos(Array.from(selectedIds)))}
            >
              Supprimer définitivement
            </button>
          </>
        }
      >
        <p className="text-sm text-neutral-700">
          Les fiches seront supprimées du site et du CMS. Les fichiers déjà envoyés (styleframes,
          miniatures) restent dans le stockage. Pour masquer une vidéo sans la perdre, utilisez plutôt
          « Dépublier ».
        </p>
        <ul className="mt-3 text-sm list-disc pl-5 max-h-40 overflow-y-auto">
          {selectedVideos.slice(0, 20).map((v) => (
            <li key={v.id}>{v.title || "Sans titre"}</li>
          ))}
          {selectedVideos.length > 20 && <li>… et {selectedVideos.length - 20} autres</li>}
        </ul>
      </Modal>
    </div>
  );
}
